import {
  Bold,
  ContextPlugin,
  Editor,
  Italic,
  Mention,
  MentionConfig,
  MultiRootEditor,
  Plugin,
} from 'ckeditor5';
import {
  ExportPdfConfig,
  SuggestionThreadView,
} from 'ckeditor5-premium-features';
import { MEDTECH_UPLOAD_URL } from '../api';
import {
  DocumentStatus,
  PageOrientation,
  QualioDocument,
} from '../api/model/document';
import { Tag } from '../api/model/tag';
import { tokenApi } from '../api/token';
import { MedtechUserV2 } from '../api/user';
import { QualioAttachmentPreviewCommand } from '../plugins/Link/QualioAttachmentPreviewCommand';
import { MultiRootEditorReactRevisionHistoryIntegration } from '../plugins/MultiRootEditorRevisionHistory/MultiRootReactRevisionHistoryIntegration';
import { isEditor, isOwner, isReviewer } from '../util/CurrentUser';
import { formatDate } from '../util/DateUtils';
import { qualioCommentThreadBuilder } from '../views/ckeditor/QualioCommentThreadView';
import { qualioCommentViewBuilder } from '../views/ckeditor/QualioCommentView';
import {
  canUserAccessAtLeastOnePrivateTag,
  getDocumentTagIdsToGroupIdsMap,
} from '../views/components/DocumentOverview/DocumentProperties/utils';
import { CKEditorConfig } from './CKEditorConfig';
import {
  PresenceListConfig,
  SidebarConfig,
} from './DocumentEditorConfig.types';
import {
  AI_COMMANDS,
  IMAGE_TOOLBAR_ITEMS,
  TABLE_TOOLBAR_ITEMS,
} from './ToolbarItems';

export function MentionLinks(editor: Editor) {
  // The upcast converter will convert a view
  //
  //		<a href="..." class="mention" data-mention="...">...</a>
  //
  // element to the model "mention" text attribute.
  editor.conversion.for('upcast').elementToAttribute({
    view: {
      name: 'span',
      classes: 'mention',
      attributes: {
        'data-mention': true,
        'data-user-id': true,
      },
    },
    model: {
      key: 'mention',
      value: (viewItem: any) => {
        return editor.plugins.get('Mention').toMentionAttribute(viewItem, {
          userId: viewItem.getAttribute('data-user-id'),
          name: viewItem.getAttribute('data-mention'),
        });
      },
    },
    converterPriority: 'high',
  });

  // Downcast the model "mention" text attribute to a view
  //
  //		<a href="..." class="mention" data-mention="...">...</a>
  //
  // element.
  editor.conversion.for('downcast').attributeToElement({
    model: 'mention',
    view: (modelAttributeValue: any, { writer }: any) => {
      // Do not convert empty attributes (lack of value means no mention).
      if (!modelAttributeValue) {
        return;
      }

      return writer.createAttributeElement(
        'span',
        {
          class: 'mention mention__user',
          'data-mention': modelAttributeValue.name,
          'data-user-id': modelAttributeValue.userId,
        },
        {
          // Make mention attribute to be wrapped by other attribute elements.
          priority: 20,
          // Prevent merging mentions together.
          id: modelAttributeValue.uid,
        },
      );
    },
    converterPriority: 'high',
  });
}

/**
 * https://docs.aws.amazon.com/bedrock/latest/userguide/model-ids.html
 */
enum AWSBedrockModelId {
  INSTANT = 'anthropic.claude-instant-v1',
  V2_1 = 'anthropic.claude-v2:1',
  V3_SONNET = 'anthropic.claude-3-sonnet-20240229-v1:0',
  V3_HAIKU = 'anthropic.claude-3-haiku-20240307-v1:0',
}

export const getUsersForComments = (users: MedtechUserV2[]) => {
  const feedArray = users.map((user) => ({
    id: '@' + user.full_name,
    userId: user.id,
    name: user.full_name,
    email: user.email,
  }));

  return (queryText: string) => {
    const isItemMatching = (item: (typeof feedArray)[number]) => {
      const searchString = queryText.toLowerCase();
      return (
        item.name.toLowerCase().includes(searchString) ||
        Boolean(item.email?.toLowerCase().includes(searchString))
      );
    };

    return new Promise((resolve) => {
      const itemsToDisplay = feedArray
        // Filter out the full list of all items to only those matching the query text.
        .filter(isItemMatching)
        // Return 10 items max - needed for generic queries when the list may contain hundreds of elements.
        .slice(0, 10)
        .sort((a, b) => a.name.localeCompare(b.name));

      resolve(itemsToDisplay);
    });
  };
};

export const getMentionableUsers = (
  qualioDocument: QualioDocument,
  users: MedtechUserV2[],
  tags: Tag[],
) => {
  const documentTagIdsToGroupIdsMap = getDocumentTagIdsToGroupIdsMap(
    qualioDocument,
    tags,
  );

  return users.filter((user) => {
    const userIdObj = { userId: user.id };
    const isReviewerOrQualityUserForDocumentForReview =
      qualioDocument.status_id === DocumentStatus.For_Review &&
      (isReviewer(userIdObj, qualioDocument) ||
        user.usergroups.includes('quality'));
    const isDocEditor = isEditor(userIdObj, qualioDocument);
    const isDocOwner = isOwner(userIdObj, qualioDocument);

    const canAccessComments =
      isReviewerOrQualityUserForDocumentForReview || isDocEditor || isDocOwner;

    // If private tags exist on document, then check if users can access any of the private tags
    if (documentTagIdsToGroupIdsMap.size > 0) {
      const canAccessDocumentTags = canUserAccessAtLeastOnePrivateTag(
        user,
        Array.from(documentTagIdsToGroupIdsMap.values()),
      );

      return canAccessComments && canAccessDocumentTags;
    }

    return canAccessComments;
  });
};

export const getMentionConfig = (mentionableUsers: MedtechUserV2[]) => {
  return {
    feeds: [
      {
        marker: '@',
        feed: getUsersForComments(mentionableUsers) as any,
        itemRenderer: (item: any) => {
          const itemElement = document.createElement('button');
          itemElement.classList.add('mention__item_user_btn');
          const fullNameElement = document.createElement('span');

          fullNameElement.classList.add('mention__item_user');
          fullNameElement.classList.add('mention__item');
          fullNameElement.textContent = item.name;
          itemElement.appendChild(fullNameElement);

          return itemElement;
        },
      },
    ],
  };
};

export class DocumentEditorConfigFactory {
  editorConfig: CKEditorConfig;

  constructor() {
    this.editorConfig = {
      simpleFileUpload: {
        url: MEDTECH_UPLOAD_URL,
      },
      simpleUpload: {
        uploadUrl: MEDTECH_UPLOAD_URL,
        withCredentials: true,
      },
      list: {
        properties: {
          styles: true,
          startIndex: false,
          reversed: false,
        },
      },
      table: {
        contentToolbar: TABLE_TOOLBAR_ITEMS,
      },
      image: {
        toolbar: IMAGE_TOOLBAR_ITEMS,
      },
      licenseKey: process.env.REACT_APP_CKE_PREMIUM_FEATURES_LICENSE_KEY,
      orientation: PageOrientation.Portrait,
      locale: {
        dateTimeFormat: formatDate,
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
      },
      indentBlock: {
        offset: 2,
        unit: 'em',
      },
      ui: {
        viewportOffset: {
          top: 180,
        },
      },
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_p',
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_h1',
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_h2',
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_h3',
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_h4',
          },
        ],
      },
    };
  }

  getConfig = () => {
    return this.editorConfig;
  };

  withNativeTableCaptionElement = () => {
    this.editorConfig.table = {
      ...this.editorConfig.table,
      tableCaption: {
        ...this.editorConfig.table?.tableCaption,
        useCaptionElement: true,
      },
    };

    return this;
  };

  withCloudService = (collaborationToken: string, bundleVersion?: string) => {
    this.editorConfig.cloudServices = {
      tokenUrl: () => Promise.resolve(collaborationToken) as any,
      uploadUrl: '',
      webSocketUrl: process.env.REACT_APP_CKE_COLLAB_WS_URL,
    };
    if (bundleVersion) {
      this.editorConfig.cloudServices.bundleVersion = bundleVersion;
    }
    return this;
  };

  withSidebar = ({ sidebarElement, preventScrollOutOfView }: SidebarConfig) => {
    this.editorConfig.sidebar = {
      container: sidebarElement as any,
      preventScrollOutOfView,
    };
    return this;
  };

  withPresenceList = ({ containerElement }: PresenceListConfig) => {
    this.editorConfig.presenceList = {
      container: containerElement as any,
      displayMe: false,
      collapseAt: 4,
    };
    return this;
  };

  withFontSize = (isMultiRoot: boolean) => {
    if (isMultiRoot) {
      this.editorConfig.fontSize = {
        options: [
          9,
          11,
          {
            title: '13',
            model: 'small',
            view: {
              name: 'span',
              attributes: {
                class: 'text-small',
              },
            },
          },
          'default',
          17,
          {
            title: '19',
            model: '19.6px',
            upcastAlso: [],
            view: '',
          },
          21,
        ],
      };
    } else {
      this.editorConfig.fontSize = {
        options: [
          'small',
          {
            title: 'Normal',
            model: '14px',
            upcastAlso: [],
            view: '',
          },
          {
            title: 'Large',
            model: '19.6px',
            upcastAlso: [],
            view: '',
          },
        ],
      };
    }
    return this;
  };

  withFontFamily = (isMultiRoot: boolean) => {
    if (isMultiRoot) {
      this.editorConfig.fontFamily = {
        options: [
          'default',
          'Arial, Helvetica, sans-serif',
          'Courier New, Courier, monospace',
          'Georgia, serif',
          'Lucida Sans Unicode, Lucida Grande, sans-serif',
          'Tahoma, Geneva, sans-serif',
          'Times New Roman, Times, serif',
          'Trebuchet MS, Helvetica, sans-serif',
          'Verdana, Geneva, sans-serif',
        ],
      }; // default font family
    } else {
      this.editorConfig.fontFamily = {
        options: ['default'],
      };
    }
    return this;
  };

  withBlockToolbar = () => {
    this.editorConfig.blockToolbar = [
      'insertTable',
      '|',
      'qualioFileUploadButton',
      'uploadImage',
      'pageBreak',
    ];
    this.editorConfig.balloonToolbar = {
      items: [
        'heading',
        'bold',
        'italic',
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        'link',
        'bookmark',
        'bulletedList',
        'numberedList',
        'qualioSectionListButton',
        'multiLevelList',
      ],
      shouldNotGroupWhenFull: true,
    };
    return this;
  };

  withEditableParentSelector = () => {
    this.editorConfig.editableParentSelector = '.editor-container__editor';
    return this;
  };

  withAI = () => {
    this.editorConfig.ai = {
      assistant: {
        adapter: {
          aws: {
            apiUrl: `${process.env.REACT_APP_AI_API_URL}/ckeditor-proxy`,
            requestParameters: {
              model: AWSBedrockModelId.V2_1,
              stream: false,
            },
          },
        },
        useTheme: false,
        commands: AI_COMMANDS,
      },
    };

    return this;
  };

  withCompanyId = (companyId: number) => {
    this.editorConfig.companyId = companyId;
    return this;
  };

  withPlugins = (plugins: (typeof Plugin | typeof ContextPlugin)[]) => {
    this.editorConfig.plugins = plugins;
    return this;
  };

  withSectionId = (sectionId: number) => {
    this.editorConfig.sectionId = sectionId;
    return this;
  };

  withDocumentId = (documentId: number) => {
    this.editorConfig.documentId = documentId;
    return this;
  };

  withSectionPosition = (position: number) => {
    this.editorConfig.position = position;
    return this;
  };

  withEntityType = (entityType: string) => {
    this.editorConfig.entityType = entityType;
    return this;
  };

  withAutosave = (onAutoSave: (editor: Editor) => Promise<void>) => {
    this.editorConfig.autosave = {
      save: onAutoSave,
      waitingTime: 1000,
    };
    return this;
  };

  withMention = (mentionConfig: MentionConfig) => {
    this.editorConfig.mention = mentionConfig;
    return this;
  };

  withCollaboration = (channelIDSuffix: string) => {
    this.editorConfig.collaboration = {
      channelId: `${process.env.REACT_APP_ENV}${
        process.env.REACT_APP_CHANNEL_ID_SUFFIX ?? ''
      }-${channelIDSuffix}`,
    };
    return this;
  };

  withPDFExport = (exportPdfConfig: ExportPdfConfig) => {
    this.editorConfig.exportPdf = {
      ...exportPdfConfig,
      converterUrl: process.env.REACT_APP_CKE_PDF_SERVICE_URL,
      tokenUrl: () => tokenApi.fetch(),
    };
    return this;
  };

  withComments = (
    commentsPermission: number,
    qualioDocument: QualioDocument,
    users: MedtechUserV2[] = [],
    tags: Tag[] = [],
    useMultiRootEditor = false,
  ) => {
    const commentThreadView = qualioCommentThreadBuilder(
      commentsPermission,
      qualioDocument,
      useMultiRootEditor,
    );
    const commentView = qualioCommentViewBuilder(useMultiRootEditor);
    const mentionableUsers = getMentionableUsers(qualioDocument, users, tags);
    this.editorConfig.comments = {
      // https://ckeditor.com/docs/ckeditor5/latest/api/module_comments_comments-CommentsConfig.html
      editorConfig: {
        extraPlugins: [Bold, Italic, Mention, MentionLinks],
        mention: getMentionConfig(mentionableUsers),
      },
      CommentView: commentView as any,
      CommentThreadView: commentThreadView as any,
    };
    return this;
  };

  withTrackChanges = (suggestionThreadView?: typeof SuggestionThreadView) => {
    this.editorConfig.trackChanges = {
      SuggestionThreadView: suggestionThreadView,
      disableComments: false,
      trackFormatChanges: 'default',
      mergeNestedSuggestions: true,
      preview: {
        renderFunction: (container, elements) => {
          container.classList.add(
            'document',
            'non-editable-mode',
            'section-reset',
          );
          container.id = 'documentHTMLViewerContent';
          elements.forEach((element) => {
            if (element.dataset.ckRootName?.includes('header')) {
              element.classList.add('section-heading');
            }
            container.appendChild(element);
          });
        },
      },
    };
    return this;
  };

  withToolbarItems = (toolbarItems: string[], useMultiRootEditor = false) => {
    if (useMultiRootEditor) {
      this.editorConfig.toolbar = {
        items: toolbarItems,
        shouldNotGroupWhenFull: false,
      };
      const container = document.querySelector(
        `.document-outline-container-${this.editorConfig.entityType}`,
      ) as HTMLElement;
      if (container) {
        this.editorConfig.documentOutline = {
          container: container,
        };
      }
    } else {
      this.editorConfig.toolbar = toolbarItems;
    }
    return this;
  };

  withLists = () => {
    this.editorConfig.list = {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    };

    return this;
  };

  withMediaEmbed = () => {
    this.editorConfig.mediaEmbed = {
      previewsInData: true,
    };

    return this;
  };

  withRevisionHistory = (
    editorContainerRef: React.RefObject<HTMLDivElement>,
    editorRevisionHistoryRef: React.RefObject<HTMLDivElement>,
    editorRevisionHistoryEditorRef: React.RefObject<HTMLDivElement>,
    docRevisionHistorySidebar: HTMLDivElement,
    editorInstanceRef: React.RefObject<MultiRootEditor>,
    toolbarElement: HTMLElement,
  ) => {
    this.editorConfig.revisionHistory = {
      editorContainer: editorContainerRef.current,
      viewerContainer: editorRevisionHistoryRef.current,
      viewerEditorElement: editorRevisionHistoryEditorRef.current,
      viewerSidebarContainer: docRevisionHistorySidebar,
      resumeUnsavedRevision: true,
      showRevisionViewerCallback: (config: any) => {
        const editorContainer = editorContainerRef.current;
        const viewerContainer = editorRevisionHistoryRef.current;
        const revisionHistoryEditorConfig = {
          ...config,
          editableParentSelector:
            '#' + editorRevisionHistoryEditorRef.current?.id,
        };

        revisionHistoryEditorConfig.plugins.push(
          MultiRootEditorReactRevisionHistoryIntegration,
        );

        return MultiRootEditor.create({}, revisionHistoryEditorConfig).then(
          (viewerEditor) => {
            if (viewerContainer && editorContainer) {
              viewerContainer.style.display = 'flex';
              editorContainer.style.display = 'none';

              const toolbarContainer = toolbarElement;
              if (toolbarContainer) {
                toolbarContainer.innerHTML = '';
                toolbarContainer.appendChild(
                  viewerEditor.ui.view.toolbar.element as HTMLElement,
                );
              }
            }

            return viewerEditor;
          },
        );
      },
      closeRevisionViewerCallback: (viewerEditor: MultiRootEditor) => {
        const editorContainer = viewerEditor.config.get(
          'revisionHistory.editorContainer',
        );
        const viewerContainer = viewerEditor.config.get(
          'revisionHistory.viewerContainer',
        );

        if (editorContainer && viewerContainer) {
          viewerContainer.style.display = 'none';
          editorContainer.style.display = '';

          return viewerEditor.destroy().then(() => {
            const toolbarContainer = toolbarElement;
            if (toolbarContainer && editorInstanceRef.current) {
              toolbarContainer.innerHTML = '';
              toolbarContainer.appendChild(
                editorInstanceRef.current.ui.view.toolbar
                  .element as HTMLElement,
              );
            }
          });
        }
      },
    };
    return this;
  };

  withPagination = (orientation: PageOrientation) => {
    if (orientation === PageOrientation.Landscape) {
      this.editorConfig.pagination = {
        pageWidth: '29.7cm',
        pageHeight: '21cm',
        pageMargins: {
          top: '41mm',
          bottom: '20mm',
          left: '20mm',
          right: '20mm',
        },
      };
    } else {
      this.editorConfig.pagination = {
        pageWidth: '21cm',
        pageHeight: '29.7cm',
        pageMargins: {
          top: '41mm',
          bottom: '20mm',
          left: '20mm',
          right: '20mm',
        },
      };
    }
    return this;
  };

  withReusableContent = (companyId: number, documentStatus: string) => {
    this.editorConfig = {
      ...this.editorConfig,
      ...CKReusableContent.ReusableContent.getReusableConfig(
        companyId,
        process.env.REACT_APP_URL_ORIGIN,
        documentStatus,
      ),
    };
    return this;
  };

  withAttachmentPreview = (onOpen: (attachmentId: string) => void) => {
    this.editorConfig = {
      ...this.editorConfig,
      link: {
        ...this.editorConfig.link,
        toolbar: [
          'linkPreview',
          '|',
          'editLink',
          'unlink',
          QualioAttachmentPreviewCommand.commandName,
        ],
      },
      image: {
        ...this.editorConfig.image,
        toolbar: [
          ...IMAGE_TOOLBAR_ITEMS,
          QualioAttachmentPreviewCommand.commandName,
        ],
      },
      attachmentPreview: { onOpen },
    };
    return this;
  };

  withOnOpenReusableContentModal = (onOpen: () => void) => {
    this.editorConfig = {
      ...this.editorConfig,
      onOpenReusableContentModal: onOpen,
    };
    return this;
  };

  withImportFromWord = () => {
    this.editorConfig = {
      ...this.editorConfig,
      importWord: {
        tokenUrl: () => tokenApi.fetch(),
        converterUrl:
          process.env.REACT_APP_CKE_EXPORT_TO_WORD_SERVICE_URL +
          '/v2/convert/docx-html',
        formatting: {
          resets: 'inline',
          defaults: 'inline',
          styles: 'inline',
        },
      },
    };
    return this;
  };
}
